import debug from 'debug'

import {
  getConfiguration,
  getRegionConfiguration,
  getPriceColumnRules,
  isRegionUS
} from '~/client/assets/js/common/render-data'

const log = debug('renderapp:render-data/price-column')

log('`renderapp` is awake')

/**
 *  Computing the price column isn't especially expensive when it's executed once,
 *  but it's executed hundreds of times per `change` event to get the same result. Ouch
 *
 *  Let's cache it
 */
export const priceColumnMap = new Map()

export default function getPriceColumn () {
  /**
   *  log('getPriceColumn')
   */

  const configuration = getConfiguration()

  let priceColumn

  /**
   *  If we have seen this configuration before then we have computed the
   *  price column before and stored it in the cache
   *
   *  Does the cache have this configuration?
   */
  if (priceColumnMap.has(configuration)) {
    /**
     *  Yes, the cache has it!
     *
     *  Let's get it from the cache
     */
    priceColumn = priceColumnMap.get(configuration)
  } else {
    /**
     *  No, the cache does not have it (we have not seen this configuration before)!
     *
     *  Let's compute it, then cache it
     *
     *  The configuration is a string whose characters may or may not be in alphabetical order
     *
     *      +A+C+D+H+E+I+F
     *
     *  Split the string on the "+" character to create an array of strings
     *
     *  Concat `regionConfiguration` first so US-only tokens (e.g. `+US`)
     *  participate in matching. Empty for non-US locales (no-op shape change).
     */
    const regionConfiguration = getRegionConfiguration() || ''
    const CONFIGURATION = (regionConfiguration + configuration).split('+')

    /**
     *  US S/R Pack price cols use `+17U+SPC` / `+48U+RPC` headers
     *  (parallels `+42U` US CUP). The US pack tiles emit `+36S` (Seven S)
     *  / `+36R` (Seven R) — separated rows r657/r658 with BE=+360 to fire
     *  the 360 cascade (matching Caterham's S=360S / R=360R mapping). The
     *  pricing col headers are unchanged, so locally swap `36S -> 17U`
     *  (when `+SPC` is set) or `36R -> 48U` (when `+RPC` is set) so
     *  `findIndex` lands on the US cols. The `+360` swap is retained
     *  defensively in case the cascade injects `+360` into the live
     *  configuration. The legacy `+175+SPC` / `+485+RPC` swap is retained
     *  so the standard models panel pricing still resolves for US users
     *  who pick those models manually.
     */
    if (isRegionUS()) {
      const tIdx = CONFIGURATION.indexOf('360')
      if (tIdx !== -1) {
        if (CONFIGURATION.includes('SPC')) CONFIGURATION[tIdx] = '17U'
        else if (CONFIGURATION.includes('RPC')) CONFIGURATION[tIdx] = '48U'
      }
      const sPackIdx = CONFIGURATION.indexOf('36S')
      if (sPackIdx !== -1 && CONFIGURATION.includes('SPC')) CONFIGURATION[sPackIdx] = '17U'
      const rPackIdx = CONFIGURATION.indexOf('36R')
      if (rPackIdx !== -1 && CONFIGURATION.includes('RPC')) CONFIGURATION[rPackIdx] = '48U'
      const sIdx = CONFIGURATION.indexOf('175')
      if (sIdx !== -1) CONFIGURATION[sIdx] = '17U'
      const rIdx = CONFIGURATION.indexOf('485')
      if (rIdx !== -1) CONFIGURATION[rIdx] = '48U'
    }

    /**
     *  `CONFIGURATION` is an array of strings
     *
     *      ['', 'A', 'C', 'D', 'H', 'E', 'I', 'F']
     *
     *   Get the price column rules as an array
     */
    const priceColumnRules = getPriceColumnRules()

    /**
     *  `priceColumnRules` is an array of strings
     *
     *      [ '+A+C+B', '+D+F+E', '+I+H+G' ]
     *
     *  Each `priceColumnRule` is a string of `+`-separated tokens. Find
     *  the rule where every token is in `CONFIGURATION`. That index is
     *  the price column.
     */
    priceColumn = priceColumnRules.findIndex((priceColumnRule) =>
      priceColumnRule.split('+').every((s) => CONFIGURATION.includes(s))
    )

    /**
     *  Now set it into the cache
     */
    priceColumnMap.set(configuration, priceColumn)
  }

  /**
   *  Return the price column
   */
  return priceColumn
}
